import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchRequestById,
  RequestItem,
  scheduleRequest,
  type CheckDetailKind,
} from "../../../api/requests";
import { fetchItCheckById, runItCheck, toRequestLike } from "../../../api/itCheck";

type Notify = (options: { message: string; variant: "success" | "error" | "info" }) => void;

interface Opts {
  kind?: CheckDetailKind;
  showNotification: Notify;
  onLoaded: (item: RequestItem) => void;
  refreshTaskList: () => Promise<unknown>;
}

export const useRequestData = (requestId: string, options: Opts) => {
  const { kind = "site", showNotification, onLoaded, refreshTaskList } = options;
  const [data, setData] = useState<RequestItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const refreshTaskListRef = useRef(refreshTaskList);
  refreshTaskListRef.current = refreshTaskList;

  useEffect(() => {
    setLoading(true);
    let cancelled = false;

    const load =
      kind === "it"
        ? fetchItCheckById(requestId).then(toRequestLike)
        : fetchRequestById(requestId);

    load
      .then((item) => {
        if (!cancelled) {
          setScheduling(false);
          setData(item);
          onLoadedRef.current(item);
        }
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [kind, requestId, showNotification]);

  const handleSchedule = useCallback(() => {
    if (!data) return;
    setScheduling(true);
    const at = new Date().toISOString();
    const run = kind === "it" ? runItCheck(data.id) : scheduleRequest(data.id, at);

    run
      .then((resp) => {
        const unavailable = resp?.status === "unavailable";
        setData((prev) =>
          prev
            ? {
                ...prev,
                available: !unavailable,
                lastPing: at,
                ...(unavailable ? {} : { status: "scheduled" }),
              }
            : prev
        );
        if (unavailable) {
          showNotification({ message: "Сайт недоступен", variant: "error" });
          return;
        }
        return refreshTaskListRef.current().then(() => {
          showNotification({ message: "Проверка успешно запущена", variant: "success" });
        });
      })
      .catch(() => undefined)
      .finally(() => setScheduling(false));
  }, [data, kind, showNotification]);

  return {
    data,
    setData,
    loading,
    scheduling,
    handleSchedule,
  };
};
